import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  QrCode, X, Save, Check, Loader, RefreshCw, History,
  ChevronUp, ChevronDown, Edit3, Trash2, Upload, AlertTriangle,
  Download, Landmark, FileText, Image, Eye, Globe, ToggleLeft, ToggleRight,
} from 'lucide-react';
import { RotateCcw } from 'lucide-react';
import Icon from './Icon';
import Button from './ui/Button';
import Input from './ui/Input';
import { applyLogoMask } from '../utils/canvasUtils';
import { QR_TEMPLATES, MARKER_TEMPLATES } from '../constants';
import { useConfirm } from '../confirmContext';

// ── SVG symbol paths ─────────────────────────────────────────
const SYMBOLS = {
  'star-of-david': { label: '\u2721', fullLabel: 'Star of David', path: 'M12 1L14.5 6.5H19.5L15.5 10L17.5 16L12 12.5L6.5 16L8.5 10L4.5 6.5H9.5Z M12 23L9.5 17.5H4.5L8.5 14L6.5 8L12 11.5L17.5 8L15.5 14L19.5 17.5H14.5Z', viewBox: '0 0 24 24' },
  cross: { label: '\u271A', fullLabel: 'Cross', path: 'M10 2H14V10H22V14H14V22H10V14H2V10H10V2Z', viewBox: '0 0 24 24' },
  menorah: { label: '\uD83D\uDD4E', fullLabel: 'Menorah', path: 'M12 2V6M12 6C12 6 8 4 6 8C4 12 4 16 4 16H6C6 16 6 12 8 10C10 8 12 6 12 6ZM12 6C12 6 14 8 16 10C18 12 18 16 18 16H20C20 16 20 12 18 8C16 4 12 6 12 6ZM4 16H20M8 16V20M16 16V20M12 16V22M6 20H18', viewBox: '0 0 24 24' },
  none: { label: '\u2014', fullLabel: 'None', path: '', viewBox: '0 0 24 24' },
};

const FONT_OPTIONS = [
  { id: 'serif', label: 'Serif', family: 'Georgia, "Times New Roman", serif' },
  { id: 'sans', label: 'Sans', family: '"Helvetica Neue", Arial, sans-serif' },
  { id: 'engraved', label: 'Engraved', family: '"Palatino Linotype", "Book Antiqua", Palatino, serif' },
];

// ── SVG marker builder ───────────────────────────────────────
function buildMarkerSVG(tpl, fields, qrPreviewUrl, fontFamily) {
  const { width, height, bg, textColor, borderColor, shape, borderRadius } = tpl;
  const qrSize = Math.min(width, height) * 0.22;
  const qrMargin = 20;
  let qrX, qrY;
  if (fields.qrPosition === 'bottom-left') { qrX = qrMargin; qrY = height - qrSize - qrMargin; }
  else if (fields.qrPosition === 'bottom-right') { qrX = width - qrSize - qrMargin; qrY = height - qrSize - qrMargin; }
  else { qrX = (width - qrSize) / 2; qrY = height - qrSize - qrMargin; }

  let shapeSvg;
  if (shape === 'arch') {
    const archR = width * 0.42;
    shapeSvg = `<path d="M0,${height} V${archR} Q0,0 ${width / 2},0 Q${width},0 ${width},${archR} V${height} Z" fill="${bg}" stroke="${borderColor}" stroke-width="3"/>`;
  } else {
    shapeSvg = `<rect x="0" y="0" width="${width}" height="${height}" rx="${borderRadius}" fill="${bg}" stroke="${borderColor}" stroke-width="3"/>`;
  }

  let y = shape === 'arch' ? 80 : 45;
  const lines = [];

  if (fields.symbol && fields.symbol !== 'none') {
    const sym = SYMBOLS[fields.symbol];
    if (sym?.path) {
      lines.push(`<g transform="translate(${width / 2 - 14}, ${y - 4}) scale(1.2)"><path d="${sym.path}" fill="${textColor}" fill-rule="evenodd"/></g>`);
      y += 38;
    }
  }
  if (fields.name) {
    lines.push(`<text x="${width / 2}" y="${y}" text-anchor="middle" font-family="${fontFamily}" font-size="28" font-weight="700" fill="${textColor}" letter-spacing="2">${esc(fields.name.toUpperCase())}</text>`);
    y += 38;
  }
  const dates = [fields.birthYear, fields.deathYear].filter(Boolean);
  if (dates.length) { lines.push(`<text x="${width / 2}" y="${y}" text-anchor="middle" font-family="${fontFamily}" font-size="18" fill="${textColor}">${dates.join(' \u2014 ')}</text>`); y += 28; }
  const places = [fields.birthplace, fields.deathPlace].filter(Boolean);
  if (places.length) { lines.push(`<text x="${width / 2}" y="${y}" text-anchor="middle" font-family="${fontFamily}" font-size="14" fill="${textColor}" opacity="0.8">${esc(places.join(' \u2014 '))}</text>`); y += 24; }
  lines.push(`<line x1="${width * 0.25}" y1="${y}" x2="${width * 0.75}" y2="${y}" stroke="${textColor}" stroke-opacity="0.3" stroke-width="1"/>`);
  y += 18;
  if (fields.epitaph) {
    wrap(fields.epitaph, 36).forEach(line => { lines.push(`<text x="${width / 2}" y="${y}" text-anchor="middle" font-family="${fontFamily}" font-size="13" font-style="italic" fill="${textColor}" opacity="0.85">${esc(line)}</text>`); y += 18; });
    y += 6;
  }
  if (fields.showFamily && fields.familyNames?.length) {
    y += 4;
    lines.push(`<text x="${width / 2}" y="${y}" text-anchor="middle" font-family="${fontFamily}" font-size="10" font-weight="600" fill="${textColor}" opacity="0.6" letter-spacing="1.5">FAMILY</text>`);
    y += 14;
    wrap(fields.familyNames.join(' \u00B7 '), 50).forEach(line => { lines.push(`<text x="${width / 2}" y="${y}" text-anchor="middle" font-family="${fontFamily}" font-size="11" fill="${textColor}" opacity="0.7">${esc(line)}</text>`); y += 15; });
  }
  const qrImg = qrPreviewUrl
    ? `<image href="${qrPreviewUrl}" x="${qrX}" y="${qrY}" width="${qrSize}" height="${qrSize}" preserveAspectRatio="xMidYMid meet"/>`
    : `<rect x="${qrX}" y="${qrY}" width="${qrSize}" height="${qrSize}" rx="4" fill="${textColor}" opacity="0.15"/><text x="${qrX + qrSize / 2}" y="${qrY + qrSize / 2 + 4}" text-anchor="middle" font-size="10" fill="${textColor}" opacity="0.4">QR</text>`;

  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}">\n${shapeSvg}\n${lines.join('\n')}\n${qrImg}\n</svg>`;
}
function esc(s) { return (s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
function wrap(text, max) { const words = text.split(/\s+/), out = []; let cur = ''; for (const w of words) { if (cur.length + w.length + 1 > max) { out.push(cur); cur = w; } else { cur = cur ? cur + ' ' + w : w; } } if (cur) out.push(cur); return out; }

// ── Client-side memorial preview HTML builder ───────────────
function buildMemorialPreviewHTML(data, config, backendUrl, root) {
  if (!data) return '<html><body style="display:flex;align-items:center;justify-content:center;height:100vh;color:#999;font-family:sans-serif">No data</body></html>';
  const themes = {
    light: { bg: '#faf9f6', text: '#1a1a1a', muted: '#666', accent: '#8b6d3f', card: '#fff', border: '#e5e5e5' },
    dark: { bg: '#1a1a1a', text: '#e8e8e8', muted: '#999', accent: '#d4a96a', card: '#2a2a2a', border: '#444' },
    sepia: { bg: '#f5f0e8', text: '#3a2a1a', muted: '#7a6a5a', accent: '#8b6d3f', card: '#faf5ed', border: '#d5c5b5' },
  };
  const t = themes[config.theme] || themes.light;
  const enabled = {};
  (config.sections || []).forEach(s => { enabled[s.id] = s.enabled !== false; });
  if (!Object.keys(enabled).length) { ['hero','biography','family','timeline','places','quotes'].forEach(k => { enabled[k] = true; }); }

  const e = s => (s || '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  const subj = data.subject || {};
  const name = subj.display_name || '';
  const born = subj.born || '';
  const death = subj.death_year || '';
  const religion = (subj.religion || '').toLowerCase();

  let html = '';

  if (enabled.hero) {
    const sym = religion.includes('jewish') || religion.includes('judaism') ? '\u2721' : religion.includes('christian') || religion.includes('catholic') ? '\u271A' : '';
    const dateLine = [born, death].filter(Boolean).join(' \u2014 ');
    const placeLine = [subj.birthplace, subj.death_place].filter(Boolean).join(' \u2014 ');
    const photoSrc = config.hero_photo ? `${backendUrl}/memorial/media/${encodeURIComponent(root)}/file/${encodeURIComponent(config.hero_photo)}` : '';
    html += `<div style="text-align:center;padding:40px 20px 30px">
      ${sym ? `<div style="font-size:2rem;margin-bottom:8px">${sym}</div>` : ''}
      ${photoSrc ? `<img src="${photoSrc}" style="width:160px;height:160px;border-radius:50%;object-fit:cover;border:3px solid ${t.accent};margin-bottom:16px" onerror="this.style.display='none'">` : ''}
      <h1 style="font-family:Georgia,serif;font-size:1.8rem;font-weight:700;letter-spacing:2px;margin:0;color:${t.text}">${e(name.toUpperCase())}</h1>
      ${dateLine ? `<p style="font-size:1rem;margin:8px 0 0;color:${t.muted}">${e(dateLine)}</p>` : ''}
      ${placeLine ? `<p style="font-size:0.85rem;margin:4px 0 0;color:${t.muted}">${e(placeLine)}</p>` : ''}
    </div><hr style="border:none;border-top:1px solid ${t.border};margin:0 30px">`;
  }

  if (enabled.biography && subj.notes) {
    const paras = subj.notes.split('\n').filter(p => p.trim()).map(p => `<p style="margin:0 0 10px;line-height:1.7">${e(p)}</p>`).join('');
    html += `<div style="padding:20px 30px"><h2 style="font-family:Georgia,serif;font-size:0.95rem;text-transform:uppercase;letter-spacing:2px;color:${t.accent};margin:0 0 12px">Biography</h2>${paras}</div><hr style="border:none;border-top:1px solid ${t.border};margin:0 30px">`;
  }

  if (enabled.family && data.family?.length) {
    const items = data.family.map(m => {
      const years = (m.birth_year || m.death_year) ? ` (${m.birth_year || '?'}\u2013${m.death_year || '?'})` : '';
      const fate = m.fate ? ` \u2014 <span style="color:${t.muted};font-size:0.85rem">${e(m.fate)}</span>` : '';
      return `<div style="padding:5px 0;border-bottom:1px solid ${t.border}"><strong style="text-transform:capitalize">${e(m.relation)}</strong>: ${e(m.name)}${e(years)}${fate}</div>`;
    }).join('');
    html += `<div style="padding:20px 30px"><h2 style="font-family:Georgia,serif;font-size:0.95rem;text-transform:uppercase;letter-spacing:2px;color:${t.accent};margin:0 0 12px">Family</h2>${items}</div><hr style="border:none;border-top:1px solid ${t.border};margin:0 30px">`;
  }

  if (enabled.timeline && data.timeline?.length) {
    const items = data.timeline.map(ev => `<div style="display:flex;gap:12px;padding:8px 0;border-bottom:1px solid ${t.border}"><div style="min-width:50px;font-weight:700;color:${t.accent};font-family:Georgia,serif">${e(String(ev.year || ''))}</div><div><strong>${e(ev.title || '')}</strong>${ev.description ? `<br><span style="color:${t.muted};font-size:0.85rem">${e(ev.description)}</span>` : ''}</div></div>`).join('');
    html += `<div style="padding:20px 30px"><h2 style="font-family:Georgia,serif;font-size:0.95rem;text-transform:uppercase;letter-spacing:2px;color:${t.accent};margin:0 0 12px">Timeline</h2>${items}</div><hr style="border:none;border-top:1px solid ${t.border};margin:0 30px">`;
  }

  if (enabled.places && data.places?.length) {
    const items = data.places.map(p => {
      const pname = p.place_name || p.canonical || '';
      const period = (p.year_from && p.year_to) ? `${p.year_from}\u2013${p.year_to}` : String(p.year_from || p.year_to || '');
      return `<div style="padding:5px 0;border-bottom:1px solid ${t.border}"><strong>${e(pname)}</strong>${period ? ` <span style="color:${t.muted}">${e(period)}</span>` : ''}${p.context ? `<br><span style="font-size:0.85rem;color:${t.muted}">${e(p.context)}</span>` : ''}</div>`;
    }).join('');
    html += `<div style="padding:20px 30px"><h2 style="font-family:Georgia,serif;font-size:0.95rem;text-transform:uppercase;letter-spacing:2px;color:${t.accent};margin:0 0 12px">Places</h2>${items}</div><hr style="border:none;border-top:1px solid ${t.border};margin:0 30px">`;
  }

  if (enabled.quotes && data.quotes?.length) {
    const items = data.quotes.map(q => {
      const text = q.text_interview || q.text_english || '';
      const textEn = q.text_english || '';
      if (!text) return '';
      return `<blockquote style="margin:0 0 14px;padding:10px 16px;border-left:3px solid ${t.accent};font-style:italic;color:${t.text}">\u201C${e(text)}\u201D${textEn && textEn !== text ? `<div style="margin-top:5px;font-size:0.85rem;font-style:normal;color:${t.muted}">\u201C${e(textEn)}\u201D</div>` : ''}</blockquote>`;
    }).join('');
    if (items) html += `<div style="padding:20px 30px"><h2 style="font-family:Georgia,serif;font-size:0.95rem;text-transform:uppercase;letter-spacing:2px;color:${t.accent};margin:0 0 12px">In Their Own Words</h2>${items}</div><hr style="border:none;border-top:1px solid ${t.border};margin:0 30px">`;
  }

  const footer = config.custom_footer || 'Giving Words to Voices\u2122 \u2014 Memorial Archive';
  html += `<div style="text-align:center;padding:24px 20px 30px;color:${t.muted};font-size:0.75rem"><p style="margin:0">${e(footer)}</p></div>`;

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:'Helvetica Neue',Arial,sans-serif;background:${t.bg};color:${t.text};font-size:14px;line-height:1.6}.mp{max-width:600px;margin:0 auto;background:${t.card}}</style></head><body><div class="mp">${html}</div></body></html>`;
}

// ══════════════════════════════════════════════════════════════
const _ts = () => { const d = new Date(); return `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}_${String(d.getHours()).padStart(2,'0')}${String(d.getMinutes()).padStart(2,'0')}${String(d.getSeconds()).padStart(2,'0')}`; };

export default function QRDesignerPanel({ theme, isDarkMode, selectedVideo, subjectName, cleanLabel, onBack, BACKEND_URL, getProjectRoot }) {

  const confirm = useConfirm();
  const [activeTab, setActiveTab] = useState('qr'); // 'qr' | 'marker' | 'memorial' | 'media'

  // Failures used to go only to console.error, so a failed save/upload looked
  // identical to "nothing happened" — the user could believe a memorial was
  // published or a photo uploaded when it was not. Each action clears this on
  // start and sets it in its catch; it is rendered next to that action's button.
  const [actionError, setActionError] = useState('');
  const errText = (err, fallback) => err?.response?.data?.detail || err?.message || fallback;

  // ── QR State ───────────────────────────────────────────────
  const [qrScope, setQrScope] = useState('global');
  const [qrStyle, setQrStyle] = useState({ fg_color: '#000000', bg_color: '#ffffff', corner_style: 'square', error_correction: 'H', quiet_zone: 4, logo_data: '', logo_size_pct: 0.25, logo_mask: 'square' });
  const [qrHasOverride, setQrHasOverride] = useState(false);
  const [qrSaving, setQrSaving] = useState(false);
  const [qrSaved, setQrSaved] = useState(false);
  const [qrPreviewUrl, setQrPreviewUrl] = useState('');
  const [qrPreviewLoading, setQrPreviewLoading] = useState(false);
  const [qrEncodeUrl, setQrEncodeUrl] = useState('https://example.com/memorial/preview');
  const [qrHistory, setQrHistory] = useState([]);
  const [qrHistoryOpen, setQrHistoryOpen] = useState(false);
  const [qrHistoryLoading, setQrHistoryLoading] = useState(false);
  const [qrRenamingId, setQrRenamingId] = useState(null);
  const [qrRenameVal, setQrRenameVal] = useState('');
  const [qrActiveTemplate, setQrActiveTemplate] = useState(null);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  const [logoMask, setLogoMask] = useState('square');
  const [logoRaw, setLogoRaw] = useState('');

  // ── Marker State ───────────────────────────────────────────
  const [markerTemplate, setMarkerTemplate] = useState('plaque');
  const [markerFields, setMarkerFields] = useState({
    name: '', birthYear: '', deathYear: '', birthplace: '', deathPlace: '',
    epitaph: '', symbol: 'star-of-david', showFamily: true,
    qrPosition: 'bottom-center', fontStyle: 'serif', familyNames: [],
  });

  // ── Memorial Page State ─────────────────────────────────────
  const [memorialData, setMemorialData] = useState(null);
  const [memorialConfig, setMemorialConfig] = useState({
    theme: 'sepia', hero_photo: '', custom_footer: '',
    sections: [
      { id: 'hero', label: 'Header', enabled: true },
      { id: 'biography', label: 'Biography', enabled: true },
      { id: 'family', label: 'Family', enabled: true },
      { id: 'timeline', label: 'Timeline', enabled: true },
      { id: 'places', label: 'Places', enabled: true },
      { id: 'quotes', label: 'Memorable Quotes', enabled: true },
    ],
  });
  const [memorialLoaded, setMemorialLoaded] = useState(false);
  const [memorialSaving, setMemorialSaving] = useState(false);
  const [memorialSaved, setMemorialSaved] = useState(false);
  const [memorialToken, setMemorialToken] = useState('');
  const memorialIframeRef = useRef(null);

  // ── Media Library State ─────────────────────────────────────
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [isDraggingMedia, setIsDraggingMedia] = useState(false);

  const updateQrStyle = (updater) => { setQrStyle(updater); setQrActiveTemplate(null); };
  const updateField = (key, val) => setMarkerFields(f => ({ ...f, [key]: val }));

  // ── Derive root ────────────────────────────────────────────
  const root = useMemo(() => {
    if (getProjectRoot && selectedVideo) return getProjectRoot(selectedVideo);
    if (!selectedVideo) return '';
    return selectedVideo.replace(/[._\s-]*(tape|part|pt|episode|ep)[._\s-]*\d+[._\s-]*(sv|en|he|pl|de|fr|ru|yi|yid|ar)?$/i, '').replace(/[._\s-]*\d+[._\s-]*(sv|en|he|pl|de|fr|ru|yi|yid|ar)?$/i, '').replace(/[._\s-]*(sv|en|he|pl|de|fr|ru|yi|yid|ar)$/i, '').replace(/[._\s-]+$/, '');
  }, [selectedVideo, getProjectRoot]);

  // ── Load subject + family ──────────────────────────────────
  useEffect(() => {
    if (!root || !axios.defaults.headers.common['Authorization']) return;
    axios.get(`${BACKEND_URL}/subject?root=${encodeURIComponent(root)}`).then(r => {
      const s = r.data;
      setMarkerFields(f => ({ ...f, name: f.name || s.display_name || '', birthYear: f.birthYear || s.born || '', deathYear: f.deathYear || s.death_year || '', birthplace: f.birthplace || s.birthplace || '', deathPlace: f.deathPlace || s.death_place || '' }));
    }).catch(() => {});
    axios.get(`${BACKEND_URL}/subject/family?root=${encodeURIComponent(root)}`).then(r => {
      const names = (r.data || []).filter(m => m.name && m.relation !== 'self').sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(m => m.name);
      setMarkerFields(f => ({ ...f, familyNames: names }));
    }).catch(() => {});
  }, [root, BACKEND_URL]);

  // ── QR API helpers ─────────────────────────────────────────
  const loadQrStyle = useCallback(async (scope, videoId) => {
    try {
      const key = scope === 'subject' && videoId ? videoId : null;
      const url = key ? `${BACKEND_URL}/qr/style/${encodeURIComponent(key)}` : `${BACKEND_URL}/qr/style`;
      const res = await axios.get(url); setQrStyle(res.data);
      if (scope === 'subject' && videoId) { try { await axios.get(`${BACKEND_URL}/qr/style/${encodeURIComponent(videoId)}/override-exists`); setQrHasOverride(true); } catch { setQrHasOverride(false); } }
      else { setQrHasOverride(false); }
    } catch (err) { console.error('loadQrStyle', err); }
  }, [BACKEND_URL]);
  const loadQrHistory = useCallback(async (scope, videoId) => {
    const key = scope === 'subject' && videoId ? videoId : '__global__'; setQrHistoryLoading(true);
    try { setQrHistory((await axios.get(`${BACKEND_URL}/qr/history/${encodeURIComponent(key)}`)).data); } catch { setQrHistory([]); } finally { setQrHistoryLoading(false); }
  }, [BACKEND_URL]);
  const fetchPreview = useCallback(async (style, encodeUrl) => {
    setQrPreviewLoading(true);
    try { const res = await axios.post(`${BACKEND_URL}/qr/preview-live`, { style, url: encodeUrl || 'https://example.com/memorial/preview' }, { responseType: 'blob' }); setQrPreviewUrl(prev => { if (prev?.startsWith('blob:')) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); }); }
    catch (err) { console.error('fetchPreview', err); setQrPreviewUrl(''); } finally { setQrPreviewLoading(false); }
  }, [BACKEND_URL]);

  useEffect(() => { if (!axios.defaults.headers.common['Authorization']) return; loadQrStyle(qrScope, selectedVideo); loadQrHistory(qrScope, selectedVideo); }, [qrScope, selectedVideo, loadQrStyle, loadQrHistory]);
  useEffect(() => { if (!axios.defaults.headers.common['Authorization']) return; const t = setTimeout(() => fetchPreview(qrStyle, qrEncodeUrl), 400); return () => clearTimeout(t); }, [qrStyle, qrEncodeUrl, fetchPreview]);

  const renameQrHistory = async (id, label) => { try { await axios.patch(`${BACKEND_URL}/qr/history/${id}/label?label=${encodeURIComponent(label)}`); setQrHistory(h => h.map(e => e.id === id ? { ...e, label } : e)); } catch {} setQrRenamingId(null); };
  const deleteQrHistory = async (id) => { try { await axios.delete(`${BACKEND_URL}/qr/history/${id}`); setQrHistory(h => h.filter(e => e.id !== id)); } catch {} };
  const restoreQrHistory = (entry) => { const style = typeof entry.style_json === 'string' ? JSON.parse(entry.style_json) : entry.style_json; setQrStyle(style); setLogoRaw(style.logo_data || ''); if (entry.encode_url) setQrEncodeUrl(entry.encode_url); setQrActiveTemplate(null); setTimeout(() => fetchPreview(style, entry.encode_url || qrEncodeUrl), 0); };
  const saveQrStyle = async () => {
    setQrSaving(true); setActionError(''); const subjectKey = (qrScope === 'subject' && selectedVideo) ? selectedVideo : '__global__';
    try { if (qrScope === 'subject' && selectedVideo) { await axios.put(`${BACKEND_URL}/qr/style/${encodeURIComponent(selectedVideo)}`, qrStyle); setQrHasOverride(true); } else { await axios.put(`${BACKEND_URL}/qr/style`, qrStyle); }
      await axios.post(`${BACKEND_URL}/qr/history/${encodeURIComponent(subjectKey)}`, { style: qrStyle, encode_url: qrEncodeUrl, label: '' });
      if (qrHistoryOpen) loadQrHistory(qrScope, selectedVideo); setQrSaved(true); setTimeout(() => setQrSaved(false), 2000);
    } catch (err) { console.error('saveQrStyle', err); setActionError(`Not saved — ${errText(err, 'the QR style could not be saved.')}`); } finally { setQrSaving(false); }
  };
  const deleteSubjectOverride = async () => { if (!selectedVideo) return; if (!await confirm({ message: `Remove override for "${subjectName || selectedVideo}"?`, title: 'Remove override?', confirmLabel: 'Remove', destructive: true })) return; try { await axios.delete(`${BACKEND_URL}/qr/style/${encodeURIComponent(selectedVideo)}`); setQrHasOverride(false); loadQrStyle('subject', selectedVideo); } catch {} };

  // ── Logo ───────────────────────────────────────────────────
  const processLogoFile = (file) => { if (!file || !['image/png','image/svg+xml','image/jpeg','image/webp'].includes(file.type)) return; const reader = new FileReader(); reader.onload = async (ev) => { const raw = ev.target.result; const masked = await applyLogoMask(raw, logoMask); setLogoRaw(raw); setQrStyle(s => { const next = { ...s, logo_data: masked, error_correction: 'H', logo_mask: logoMask }; setTimeout(() => fetchPreview(next, qrEncodeUrl), 0); return next; }); }; reader.readAsDataURL(file); };
  const changeMask = async (newMask) => { setLogoMask(newMask); if (logoRaw) { const masked = await applyLogoMask(logoRaw, newMask); setQrStyle(s => { const next = { ...s, logo_data: masked, logo_mask: newMask }; setTimeout(() => fetchPreview(next, qrEncodeUrl), 0); return next; }); } else { setQrStyle(s => ({ ...s, logo_mask: newMask })); } };
  const handleLogoUpload = (e) => processLogoFile(e.target.files?.[0]);
  const handleLogoDrop = (e) => { e.preventDefault(); setIsDraggingLogo(false); processLogoFile(e.dataTransfer.files?.[0]); };
  const handleLogoDragOver = (e) => { e.preventDefault(); setIsDraggingLogo(true); };
  const handleLogoDragLeave = () => setIsDraggingLogo(false);

  // ── QR as data URI (for embedding inside marker SVG) ───────
  const [qrDataUrl, setQrDataUrl] = useState('');
  useEffect(() => {
    if (!qrPreviewUrl) { setQrDataUrl(''); return; }
    (async () => {
      try {
        const resp = await fetch(qrPreviewUrl);
        const text = await resp.text();
        const b64 = btoa(unescape(encodeURIComponent(text)));
        setQrDataUrl('data:image/svg+xml;base64,' + b64);
      } catch { setQrDataUrl(''); }
    })();
  }, [qrPreviewUrl]);

  // ── Marker SVG ─────────────────────────────────────────────
  const activeTpl = MARKER_TEMPLATES.find(t => t.id === markerTemplate) || MARKER_TEMPLATES[0];
  const fontFamily = (FONT_OPTIONS.find(f => f.id === markerFields.fontStyle) || FONT_OPTIONS[0]).family;
  const markerSvgStr = useMemo(() => buildMarkerSVG(activeTpl, markerFields, qrDataUrl, fontFamily), [activeTpl, markerFields, qrDataUrl, fontFamily]);
  const exportMarkerSVG = () => { const blob = new Blob([markerSvgStr], { type: 'image/svg+xml' }); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `${(markerFields.name || 'marker').replace(/\s+/g, '_')}_gravemarker_${_ts()}.svg`; a.click(); URL.revokeObjectURL(url); };

  // ── PNG Export (print-ready at 3x) ──────────────────────────
  const exportAsPNG = useCallback((svgString, filename, scale = 3) => {
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgString, 'image/svg+xml');
    const svgEl = svgDoc.documentElement;
    const w = parseFloat(svgEl.getAttribute('width')) || 600;
    const h = parseFloat(svgEl.getAttribute('height')) || 600;
    const canvas = document.createElement('canvas');
    canvas.width = w * scale;
    canvas.height = h * scale;
    const ctx = canvas.getContext('2d');
    const img = new window.Image();
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(pngBlob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(pngBlob);
        a.download = filename;
        a.click();
        URL.revokeObjectURL(a.href);
      }, 'image/png');
    };
    img.src = url;
  }, []);

  const exportMarkerPNG = () => exportAsPNG(markerSvgStr, `${(markerFields.name || 'marker').replace(/\s+/g, '_')}_gravemarker.png`);
  const exportQRPNG = useCallback(async () => {
    if (!qrPreviewUrl) return;
    try {
      const resp = await fetch(qrPreviewUrl);
      const svgText = await resp.text();
      exportAsPNG(svgText, `${(root || 'qr').replace(/\s+/g, '_')}_qr.png`);
    } catch { /* fallback: just download the blob as-is */ }
  }, [qrPreviewUrl, root, exportAsPNG]);

  // ── Memorial Page API helpers ──────────────────────────────
  const loadMemorialData = useCallback(async () => {
    if (!root || !axios.defaults.headers.common['Authorization']) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/memorial/page-data/${encodeURIComponent(root)}`);
      setMemorialData(res.data);
      if (res.data.page_config && typeof res.data.page_config === 'object' && Object.keys(res.data.page_config).length) {
        setMemorialConfig(prev => ({ ...prev, ...res.data.page_config }));
      }
      if (res.data.public_token) setMemorialToken(res.data.public_token);
      setMemorialLoaded(true);
    } catch (err) { console.error('loadMemorialData', err); setMemorialLoaded(true); }
  }, [root, BACKEND_URL]);

  const saveMemorialPage = async () => {
    setMemorialSaving(true);
    setActionError('');
    try {
      const res = await axios.post(`${BACKEND_URL}/memorial/page/${encodeURIComponent(root)}`, memorialConfig);
      if (res.data.token) setMemorialToken(res.data.token);
      setMemorialSaved(true);
      setTimeout(() => setMemorialSaved(false), 2000);
    } catch (err) { console.error('saveMemorialPage', err); setActionError(`Not published — ${errText(err, 'the memorial page could not be saved.')}`); }
    finally { setMemorialSaving(false); }
  };

  const exportMemorialHTML = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/memorial/export-html/${encodeURIComponent(root)}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(memorialData?.subject?.display_name || root).replace(/\s+/g, '_')}_memorial_${_ts()}.html`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) { console.error('exportMemorialHTML', err); }
  };

  useEffect(() => { if (activeTab === 'memorial' && !memorialLoaded) loadMemorialData(); }, [activeTab, memorialLoaded, loadMemorialData]);

  // ── Media Library API helpers ──────────────────────────────
  const loadMedia = useCallback(async () => {
    if (!root || !axios.defaults.headers.common['Authorization']) return;
    setMediaLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/memorial/media/${encodeURIComponent(root)}`);
      setMediaFiles(res.data || []);
    } catch { setMediaFiles([]); }
    finally { setMediaLoading(false); }
  }, [root, BACKEND_URL]);

  const uploadMedia = async (file) => {
    if (!file || mediaUploading) return;
    setMediaUploading(true);
    setActionError('');
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${BACKEND_URL}/memorial/media/${encodeURIComponent(root)}`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      loadMedia();
    } catch (err) { console.error('uploadMedia', err); setActionError(`Upload failed — ${errText(err, 'the file was not uploaded.')}`); }
    finally { setMediaUploading(false); }
  };

  const deleteMedia = async (filename) => {
    if (!await confirm({ message: `Delete "${filename}"?`, title: 'Delete file?', confirmLabel: 'Delete', destructive: true })) return;
    setActionError('');
    try {
      await axios.delete(`${BACKEND_URL}/memorial/media/${encodeURIComponent(root)}/${encodeURIComponent(filename)}`);
      setMediaFiles(f => f.filter(m => m.filename !== filename));
    } catch (err) { console.error('deleteMedia', err); setActionError(`Delete failed — ${errText(err, `"${filename}" was not removed.`)}`); }
  };

  const handleMediaDrop = (e) => { e.preventDefault(); setIsDraggingMedia(false); const file = e.dataTransfer.files?.[0]; if (file) uploadMedia(file); };

  useEffect(() => { if (activeTab === 'media') loadMedia(); }, [activeTab, loadMedia]);

  // ── Tab button helper (matches main nav pill style) ────────
  const accent = theme.glossaryAccent || '#6366f1';

  // Shown directly under whichever action failed (only one tab renders at a time).
  const errorBanner = actionError ? (
    <div role="alert" style={{
      flexShrink: 0, fontSize: '0.62rem', fontWeight: 600, color: '#ef4444', lineHeight: 1.4,
      background: '#ef444414', border: '1px solid #ef444444', borderRadius: '7px', padding: '6px 9px',
    }}>
      {actionError}
    </div>
  ) : null;
  const tabBtn = (id, icon, label) => (
    <button key={id} onClick={() => setActiveTab(id)} style={{
      display: 'flex', alignItems: 'center', gap: '5px', padding: '5px 12px',
      background: activeTab === id ? accent : theme.bg,
      color: activeTab === id ? '#fff' : theme.text,
      border: `1px solid ${activeTab === id ? accent : theme.border}`,
      borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold',
      fontSize: '0.72rem', transition: '0.15s',
    }}><Icon icon={icon} size={13} /> {label.toUpperCase()}</button>
  );

  // ══════════════════════════════════════════════════════════
  return (
    <div style={{ display: 'flex', height: '100%', width: '100%', background: theme.bg, overflow: 'hidden' }}>

      {/* Left spacer */}
      <div style={{ flex: 1 }} />

      {/* Center column */}
      <div style={{ display: 'flex', flexDirection: 'column', flex: '0 1 1100px', overflow: 'hidden' }}>

        {/* ── Tab bar (folder-tab, matching main nav) ── */}
        <div style={{ display: 'flex', justifyContent: 'center', background: theme.bg, flexShrink: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '6px 8px 7px', minHeight: '40px', boxSizing: 'border-box',
            background: theme.sidebar,
            border: `1px solid ${theme.border}`, borderTop: 'none',
            borderRadius: '0 0 14px 14px',
            boxShadow: isDarkMode ? '0 8px 18px rgba(0,0,0,0.18)' : '0 8px 18px rgba(15,23,42,0.08)',
          }}>
            {tabBtn('qr', QrCode, 'QR Code')}
            {tabBtn('marker', Landmark, 'Gravemarker')}
            {tabBtn('memorial', FileText, 'Memorial Page')}
            {tabBtn('media', Image, 'Media Library')}
          </div>
        </div>

        {/* ══ TAB: QR Code ══════════════════════════════════════ */}
        {activeTab === 'qr' && (
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* LEFT — QR controls */}
          <div style={{ width: '340px', flexShrink: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>

            {/* QR Templates */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '8px' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', justifyContent: 'space-between' }}>
                <span>Templates</span>
                {qrActiveTemplate && <span style={{ color: accent, fontWeight: 400, fontStyle: 'italic' }}>{QR_TEMPLATES.find(t => t.id === qrActiveTemplate)?.name}</span>}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '5px' }}>
                {QR_TEMPLATES.map(tpl => {
                  const isActive = qrActiveTemplate === tpl.id;
                  return (
                    <button key={tpl.id} onClick={() => { const m = { ...tpl.style, logo_data: qrStyle.logo_data, logo_size_pct: qrStyle.logo_size_pct }; if (m.logo_data) m.error_correction = 'H'; setQrStyle(m); setQrActiveTemplate(tpl.id); }} title={tpl.desc}
                      style={{ padding: '5px 2px 4px', background: isActive ? `${accent}15` : theme.inputBg, border: `2px solid ${isActive ? accent : theme.border}`, borderRadius: '7px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                      <div style={{ width: '28px', height: '28px', borderRadius: '4px', background: tpl.swatch.bg, border: `1px solid ${theme.border}`, display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gridTemplateRows: 'repeat(5,1fr)', padding: '3px', gap: '1px', boxSizing: 'border-box' }}>
                        {[1,1,1,0,1,1,0,1,1,0,1,1,1,0,1,0,1,0,1,1,1,0,1,1,0].map((on, i) => (<div key={i} style={{ background: on ? tpl.swatch.fg : 'transparent', borderRadius: tpl.style.corner_style === 'dots' ? '50%' : tpl.style.corner_style === 'rounded' ? '1px' : '0' }} />))}
                      </div>
                      <span style={{ fontSize: '0.52rem', color: isActive ? accent : theme.textSub, fontWeight: isActive ? 700 : 400, lineHeight: 1.1, textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%' }}>{tpl.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Scope */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
              <div style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
                <span style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0, marginRight: '4px' }}>Scope</span>
                {[['global', 'Global Default'], ['subject', subjectName || 'This Subject']].map(([val, label]) => (
                  <button key={val} onClick={() => setQrScope(val)} style={{ flex: 1, padding: '5px', fontSize: theme.fsBadge, background: qrScope === val ? accent : theme.inputBg, color: qrScope === val ? '#fff' : theme.textSub, border: `1px solid ${qrScope === val ? accent : theme.border}`, borderRadius: '5px', cursor: 'pointer', fontWeight: qrScope === val ? 700 : 400 }}>{label}</button>
                ))}
              </div>
              {qrScope === 'subject' && selectedVideo && (
                <div style={{ marginTop: '4px', fontSize: '0.6rem', color: theme.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {subjectName || cleanLabel(selectedVideo)}
                  {qrHasOverride ? <button onClick={deleteSubjectOverride} style={{ marginLeft: '6px', background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontSize: '0.6rem', display: 'inline-flex', alignItems: 'center', gap: '3px' }}><Icon icon={Trash2} size={10} />remove override</button>
                  : <span style={{ marginLeft: '5px', color: `${accent}88`, fontStyle: 'italic' }}>(inheriting global)</span>}
                </div>
              )}
            </div>

            {/* Colors + Corners */}
            <div style={{ display: 'flex', gap: '6px' }}>
              <div style={{ flex: 1, background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '5px', textTransform: 'uppercase' }}>Colors</div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  {[['fg_color', 'FG'], ['bg_color', 'BG']].map(([key, label]) => (
                    <label key={key} style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                      <input type="color" value={qrStyle[key]} onChange={e => updateQrStyle(s => ({ ...s, [key]: e.target.value }))} style={{ width: '28px', height: '28px', padding: '1px', border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', background: 'none', flexShrink: 0 }} />
                      <div><div style={{ fontSize: '0.6rem', color: theme.textSub, fontWeight: 600 }}>{label}</div><code style={{ fontSize: '0.55rem', color: theme.textSub }}>{qrStyle[key]}</code></div>
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ flex: 1, background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '5px', textTransform: 'uppercase' }}>Corners</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[['square', '▪'], ['rounded', '▢'], ['dots', '●']].map(([val, icon]) => (
                    <button key={val} onClick={() => updateQrStyle(s => ({ ...s, corner_style: val }))} title={val}
                      style={{ flex: 1, padding: '5px 2px', fontSize: '1rem', background: qrStyle.corner_style === val ? accent : theme.inputBg, color: qrStyle.corner_style === val ? '#fff' : theme.textSub, border: `1px solid ${qrStyle.corner_style === val ? accent : theme.border}`, borderRadius: '5px', cursor: 'pointer' }}>{icon}</button>
                  ))}
                </div>
              </div>
            </div>

            {/* EC + Quiet */}
            <div style={{ display: 'flex', gap: '6px' }}>
              <div style={{ flex: 1, background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '5px', textTransform: 'uppercase' }}>EC {qrStyle.logo_data && <span style={{ color: theme.warning, fontWeight: 400, fontSize: '0.55rem' }}>forced H</span>}</div>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {['L','M','Q','H'].map(lvl => (<button key={lvl} disabled={!!qrStyle.logo_data} onClick={() => updateQrStyle(s => ({ ...s, error_correction: lvl }))} style={{ flex: 1, padding: '5px 2px', fontSize: theme.fsBadge, background: qrStyle.error_correction === lvl ? accent : theme.inputBg, color: qrStyle.error_correction === lvl ? '#fff' : theme.textSub, border: `1px solid ${qrStyle.error_correction === lvl ? accent : theme.border}`, borderRadius: '5px', cursor: qrStyle.logo_data ? 'not-allowed' : 'pointer', opacity: qrStyle.logo_data && lvl !== 'H' ? 0.35 : 1 }}>{lvl}</button>))}
                </div>
                <div style={{ marginTop: '3px', fontSize: '0.52rem', color: theme.textSub }}>L7·M15·Q25·H30%</div>
              </div>
              <div style={{ flex: 1, background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}><span style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, textTransform: 'uppercase' }}>Quiet</span><span style={{ fontSize: theme.fsBadge, color: accent, fontWeight: 700 }}>{qrStyle.quiet_zone}</span></div>
                <input type="range" min={0} max={10} step={1} value={qrStyle.quiet_zone} onChange={e => updateQrStyle(s => ({ ...s, quiet_zone: Number(e.target.value) }))} style={{ width: '100%', accentColor: accent }} />
              </div>
            </div>

            {/* Logo */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, textTransform: 'uppercase' }}>Center Logo</span>
                <div style={{ display: 'flex', gap: '4px' }}>
                  {[['square','▪','Square'],['circle','●','Circle'],['rect','▬','Rect']].map(([val, icon, label]) => (<button key={val} onClick={() => changeMask(val)} title={label} style={{ padding: '3px 6px', fontSize: '0.75rem', background: logoMask === val ? accent : theme.inputBg, color: logoMask === val ? '#fff' : theme.textSub, border: `1px solid ${logoMask === val ? accent : theme.border}`, borderRadius: '4px', cursor: 'pointer' }}>{icon}</button>))}
                </div>
              </div>
              {qrStyle.logo_data ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <div style={{ width: '44px', height: logoMask === 'rect' ? '28px' : '44px', flexShrink: 0, background: '#fff', borderRadius: logoMask === 'circle' ? '50%' : '5px', border: `1px solid ${theme.border}`, overflow: 'hidden' }}><img src={qrStyle.logo_data} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>
                  <div style={{ flex: 1 }}><div style={{ fontSize: '0.6rem', color: theme.textSub, marginBottom: '2px' }}>Size: {Math.round(qrStyle.logo_size_pct * 100)}%</div><input type="range" min={15} max={35} step={1} value={Math.round(qrStyle.logo_size_pct * 100)} onChange={e => updateQrStyle(s => ({ ...s, logo_size_pct: Number(e.target.value) / 100 }))} style={{ width: '100%', accentColor: accent }} /></div>
                  <button onClick={() => { setQrStyle(s => ({ ...s, logo_data: '' })); setLogoRaw(''); }} style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', flexShrink: 0 }} title="Remove"><Icon icon={Trash2} size={13} /></button>
                </div>
              ) : (
                <label onDrop={handleLogoDrop} onDragOver={handleLogoDragOver} onDragLeave={handleLogoDragLeave} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '8px', border: `2px dashed ${isDraggingLogo ? accent : theme.border}`, borderRadius: '6px', cursor: 'pointer', background: isDraggingLogo ? `${accent}12` : 'transparent' }}>
                  <Icon icon={Upload} size={16} color={isDraggingLogo ? accent : theme.textSub} />
                  <span style={{ fontSize: '0.6rem', color: isDraggingLogo ? accent : theme.textSub }}>{isDraggingLogo ? 'Drop to upload' : 'Drag & drop or click'}</span>
                  <input type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" onChange={handleLogoUpload} style={{ display: 'none' }} />
                </label>
              )}
              {qrStyle.logo_data && <div style={{ fontSize: '0.55rem', color: theme.warning, display: 'flex', alignItems: 'center', gap: '4px' }}><Icon icon={AlertTriangle} size={10} />EC auto-set to H</div>}
            </div>

            {/* URL */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase' }}>URL to encode</div>
              <Input theme={theme} value={qrEncodeUrl} onChange={e => setQrEncodeUrl(e.target.value)} placeholder="https://..." style={{ fontFamily: 'monospace' }} />
            </div>
          </div>

          {/* Divider */}
          <div style={{ width: '1px', background: theme.border, margin: '20px 0', flexShrink: 0, alignSelf: 'flex-start', height: 'calc(100% - 140px)' }} />

          {/* RIGHT — QR preview + history + save */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '14px', gap: '10px', overflow: 'hidden' }}>
            <div style={{ flex: 1, minHeight: 0, maxHeight: '480px', display: 'flex', flexDirection: 'column', background: theme.card, borderRadius: '8px', padding: '10px', overflow: 'hidden' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '8px', textTransform: 'uppercase', flexShrink: 0 }}>Live Preview</div>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', borderRadius: '6px', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
                {qrPreviewLoading ? <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', color: '#aaa' }}><Icon icon={Loader} size={24} /><span style={{ fontSize: theme.fsBadge }}>Generating...</span></div>
                : qrPreviewUrl ? <img src={qrPreviewUrl} alt="QR preview" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                : <div style={{ color: '#aaa', fontSize: theme.fsBadge }}>Preview unavailable</div>}
              </div>
              <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexShrink: 0 }}>
                <Button theme={theme} variant="secondary" size="sm" icon={RefreshCw} onClick={() => fetchPreview(qrStyle, qrEncodeUrl)} disabled={qrPreviewLoading} style={{ flex: 1 }}>Refresh</Button>
                <Button theme={theme} variant="secondary" size="sm" icon={Download} onClick={exportQRPNG} disabled={!qrPreviewUrl} title="Download as PNG (print-ready 3×)">PNG</Button>
              </div>
            </div>

            {/* Saved Versions */}
            <div style={{ flexShrink: 0, border: `1px solid ${theme.border}`, borderRadius: '8px', overflow: 'hidden' }}>
              <button onClick={() => { const next = !qrHistoryOpen; setQrHistoryOpen(next); if (next && qrHistory.length === 0) loadQrHistory(qrScope, selectedVideo); }}
                style={{ width: '100%', padding: '8px 10px', background: theme.card, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: theme.text }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: theme.fsBadge, fontWeight: 700, textTransform: 'uppercase' }}><Icon icon={History} size={12} /> Saved Versions {qrHistory.length > 0 && <span style={{ background: accent, color: '#fff', borderRadius: '10px', padding: '0 6px', fontSize: '0.6rem' }}>{qrHistory.length}</span>}</span>
                <Icon icon={qrHistoryOpen ? ChevronUp : ChevronDown} size={13} color={theme.textSub} />
              </button>
              {qrHistoryOpen && (
                <div style={{ maxHeight: '220px', overflowY: 'auto', background: theme.inputBg }}>
                  {qrHistoryLoading ? <div style={{ padding: '16px', textAlign: 'center', color: theme.textSub, fontSize: theme.fsBadge }}><Icon icon={Loader} size={14} /> Loading...</div>
                  : qrHistory.length === 0 ? <div style={{ padding: '14px', textAlign: 'center', color: theme.textSub, fontSize: theme.fsBadge }}>No saved versions yet.</div>
                  : qrHistory.map((entry, idx) => {
                    const svgBlob = new Blob([entry.svg_data], { type: 'image/svg+xml' }); const svgUrl = URL.createObjectURL(svgBlob);
                    const ts = new Date(entry.created_at).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
                    const isRenaming = qrRenamingId === entry.id;
                    return (
                      <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 10px', borderTop: idx === 0 ? 'none' : `1px solid ${theme.border}` }}>
                        <div onClick={() => restoreQrHistory(entry)} style={{ width: 44, height: 44, flexShrink: 0, borderRadius: '5px', overflow: 'hidden', border: `1px solid ${theme.border}`, cursor: 'pointer', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><img src={svgUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain' }} onLoad={() => URL.revokeObjectURL(svgUrl)} /></div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {isRenaming ? <Input theme={theme} size="sm" autoFocus value={qrRenameVal} onChange={e => setQrRenameVal(e.target.value)} onBlur={() => renameQrHistory(entry.id, qrRenameVal)} onKeyDown={e => { if (e.key === 'Enter') renameQrHistory(entry.id, qrRenameVal); if (e.key === 'Escape') setQrRenamingId(null); }} style={{ padding: '3px 6px' }} />
                          : <div style={{ fontSize: theme.fsBadge, fontWeight: 600, color: theme.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{entry.label || ts}</div>}
                          <div style={{ fontSize: '0.6rem', color: theme.textSub, marginTop: '2px' }}>{ts}</div>
                        </div>
                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                          <button onClick={() => restoreQrHistory(entry)} title="Restore" style={{ background: `${accent}15`, border: `1px solid ${accent}40`, borderRadius: '4px', padding: '3px 6px', cursor: 'pointer', color: accent, fontSize: theme.fsBadge, display: 'flex', alignItems: 'center', gap: '3px' }}><Icon icon={RotateCcw} size={10} /> Restore</button>
                          <button onClick={() => { setQrRenamingId(entry.id); setQrRenameVal(entry.label || ''); }} title="Rename" style={{ background: 'none', border: `1px solid ${theme.border}`, borderRadius: '4px', padding: '3px 5px', cursor: 'pointer', color: theme.textSub }}><Icon icon={Edit3} size={10} /></button>
                          <button onClick={() => deleteQrHistory(entry.id)} title="Delete" style={{ background: 'none', border: '1px solid #ef444444', borderRadius: '4px', padding: '3px 5px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', gap: '3px', fontSize: '0.6rem', fontWeight: 600 }}><Icon icon={Trash2} size={10} />Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Save */}
            <button onClick={saveQrStyle} disabled={qrSaving} style={{ width: '100%', padding: '11px', flexShrink: 0, background: qrSaved ? theme.success : accent, color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: qrSaving ? 'wait' : 'pointer', fontSize: theme.fsButton, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
              {qrSaved ? <><Icon icon={Check} size={14} /> SAVED</> : qrSaving ? <><Icon icon={Loader} size={14} /> SAVING...</> : <><Icon icon={Save} size={14} /> {qrScope === 'subject' ? 'SAVE SUBJECT OVERRIDE' : 'SAVE GLOBAL DEFAULT'}</>}
            </button>
            {errorBanner}
            <div style={{ fontSize: '0.6rem', color: theme.textSub, textAlign: 'center', lineHeight: 1.4, flexShrink: 0 }}>{qrScope === 'global' ? 'Archive-wide default for all memorial QR codes.' : `Override applies only to ${subjectName || 'the currently selected subject'}.`}</div>
          </div>
        </div>
      )}

      {/* ══ TAB: Gravemarker ══════════════════════════════════ */}
      {activeTab === 'marker' && (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden', maxWidth: '1100px', margin: '0 auto', width: '100%' }}>

          {/* LEFT — Marker settings */}
          <div style={{ width: '340px', flexShrink: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>

            {/* Template picker */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '8px' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '7px', textTransform: 'uppercase' }}>Marker Template</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '5px' }}>
                {MARKER_TEMPLATES.map(tpl => {
                  const isActive = markerTemplate === tpl.id;
                  const aspect = tpl.height / tpl.width;
                  return (
                    <button key={tpl.id} onClick={() => setMarkerTemplate(tpl.id)} title={tpl.desc}
                      style={{ padding: '6px 4px', background: isActive ? `${accent}15` : theme.inputBg, border: `2px solid ${isActive ? accent : theme.border}`, borderRadius: '7px', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                      <div style={{ width: '36px', height: `${36 * aspect}px`, borderRadius: tpl.shape === 'arch' ? '36px 36px 2px 2px' : `${tpl.borderRadius}px`, background: tpl.bg, border: `1px solid ${tpl.borderColor}` }} />
                      <span style={{ fontSize: '0.52rem', color: isActive ? accent : theme.textSub, fontWeight: isActive ? 700 : 400 }}>{tpl.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Name */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase' }}>Name</div>
              <Input theme={theme} value={markerFields.name} onChange={e => updateField('name', e.target.value)} />
            </div>

            {/* Dates */}
            <div style={{ display: 'flex', gap: '6px' }}>
              <div style={{ flex: 1, background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase' }}>Born</div>
                <Input theme={theme} value={markerFields.birthYear} onChange={e => updateField('birthYear', e.target.value)} placeholder="1925" />
              </div>
              <div style={{ flex: 1, background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase' }}>Died</div>
                <Input theme={theme} value={markerFields.deathYear} onChange={e => updateField('deathYear', e.target.value)} placeholder="2024" />
              </div>
            </div>

            {/* Places */}
            <div style={{ display: 'flex', gap: '6px' }}>
              <div style={{ flex: 1, background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase' }}>Birthplace</div>
                <Input theme={theme} value={markerFields.birthplace} onChange={e => updateField('birthplace', e.target.value)} />
              </div>
              <div style={{ flex: 1, background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase' }}>Death Place</div>
                <Input theme={theme} value={markerFields.deathPlace} onChange={e => updateField('deathPlace', e.target.value)} />
              </div>
            </div>

            {/* Epitaph */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase' }}>Epitaph</div>
              <Input theme={theme} multiline value={markerFields.epitaph} onChange={e => updateField('epitaph', e.target.value)} rows={2} placeholder="In loving memory..." style={{ minHeight: '40px' }} />
            </div>

            {/* Symbol */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '5px', textTransform: 'uppercase' }}>Symbol</div>
              <div style={{ display: 'flex', gap: '4px' }}>
                {Object.entries(SYMBOLS).map(([key, sym]) => (
                  <button key={key} onClick={() => updateField('symbol', key)} title={sym.fullLabel}
                    style={{ flex: 1, padding: '6px 2px', fontSize: '1rem', background: markerFields.symbol === key ? accent : theme.inputBg, color: markerFields.symbol === key ? '#fff' : theme.textSub, border: `1px solid ${markerFields.symbol === key ? accent : theme.border}`, borderRadius: '5px', cursor: 'pointer' }}>
                    {sym.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Font + QR Position */}
            <div style={{ display: 'flex', gap: '6px' }}>
              <div style={{ flex: 1, background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '5px', textTransform: 'uppercase' }}>Font</div>
                <div style={{ display: 'flex', gap: '3px' }}>
                  {FONT_OPTIONS.map(f => (<button key={f.id} onClick={() => updateField('fontStyle', f.id)} style={{ flex: 1, padding: '4px 2px', fontSize: theme.fsBadge, fontFamily: f.family, background: markerFields.fontStyle === f.id ? accent : theme.inputBg, color: markerFields.fontStyle === f.id ? '#fff' : theme.textSub, border: `1px solid ${markerFields.fontStyle === f.id ? accent : theme.border}`, borderRadius: '4px', cursor: 'pointer' }}>{f.label}</button>))}
                </div>
              </div>
              <div style={{ flex: 1, background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '5px', textTransform: 'uppercase' }}>QR Pos</div>
                <div style={{ display: 'flex', gap: '3px' }}>
                  {[['bottom-left', 'L'], ['bottom-center', 'C'], ['bottom-right', 'R']].map(([val, label]) => (<button key={val} onClick={() => updateField('qrPosition', val)} style={{ flex: 1, padding: '4px 2px', fontSize: theme.fsBadge, background: markerFields.qrPosition === val ? accent : theme.inputBg, color: markerFields.qrPosition === val ? '#fff' : theme.textSub, border: `1px solid ${markerFields.qrPosition === val ? accent : theme.border}`, borderRadius: '4px', cursor: 'pointer' }}>{label}</button>))}
                </div>
              </div>
            </div>

            {/* Family */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, textTransform: 'uppercase' }}>Family Names</span>
                <button onClick={() => updateField('showFamily', !markerFields.showFamily)} style={{ padding: '3px 8px', fontSize: theme.fsBadge, background: markerFields.showFamily ? accent : theme.inputBg, color: markerFields.showFamily ? '#fff' : theme.textSub, border: `1px solid ${markerFields.showFamily ? accent : theme.border}`, borderRadius: '4px', cursor: 'pointer', fontWeight: 700 }}>{markerFields.showFamily ? 'ON' : 'OFF'}</button>
              </div>
              {markerFields.showFamily && markerFields.familyNames.length > 0 && (
                <div style={{ marginTop: '6px', fontSize: '0.6rem', color: theme.textSub, lineHeight: 1.6 }}>{markerFields.familyNames.join(' \u00B7 ')}</div>
              )}
            </div>
          </div>

          {/* Divider */}
          <div style={{ width: '1px', background: theme.border, margin: '20px 0', flexShrink: 0, alignSelf: 'flex-start', height: 'calc(100% - 140px)' }} />

          {/* RIGHT — Marker preview */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px', overflow: 'auto', background: isDarkMode ? '#111' : '#e8e8e8' }}>
            <div style={{ maxWidth: activeTpl.width + 'px', filter: 'drop-shadow(0 8px 32px rgba(0,0,0,0.3))' }} dangerouslySetInnerHTML={{ __html: markerSvgStr }} />
            <div style={{ display: 'flex', gap: '8px', marginTop: '14px' }}>
              <button onClick={exportMarkerSVG} style={{ padding: '8px 20px', background: accent, color: '#fff', border: 'none', borderRadius: '7px', fontWeight: 700, fontSize: theme.fsButton, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Download size={14} /> Export SVG
              </button>
              <Button theme={theme} variant="secondary" size="md" icon={Download} onClick={exportMarkerPNG}>Export PNG</Button>
            </div>
          </div>
        </div>
      )}

      {/* ══ TAB: Memorial Page ═══════════════════════════════════ */}
      {activeTab === 'memorial' && (
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* LEFT — Page settings */}
          <div style={{ width: '340px', flexShrink: 0, overflowY: 'auto', padding: '10px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>

            {/* Theme */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '5px', textTransform: 'uppercase' }}>Page Theme</div>
              <div style={{ display: 'flex', gap: '4px' }}>
                {[['light', 'Light', '#faf9f6'], ['dark', 'Dark', '#1a1a1a'], ['sepia', 'Sepia', '#f5f0e8']].map(([val, label, bg]) => (
                  <button key={val} onClick={() => setMemorialConfig(c => ({ ...c, theme: val }))}
                    style={{ flex: 1, padding: '6px 4px', fontSize: theme.fsBadge, background: memorialConfig.theme === val ? accent : theme.inputBg, color: memorialConfig.theme === val ? '#fff' : theme.textSub, border: `1px solid ${memorialConfig.theme === val ? accent : theme.border}`, borderRadius: '5px', cursor: 'pointer', fontWeight: memorialConfig.theme === val ? 700 : 400 }}>
                    <div style={{ width: 14, height: 14, borderRadius: '50%', background: bg, border: '1px solid #999', margin: '0 auto 3px' }} />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Hero Photo */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '5px', textTransform: 'uppercase' }}>Hero Photo</div>
              {mediaFiles.length > 0 ? (
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  <button onClick={() => setMemorialConfig(c => ({ ...c, hero_photo: '' }))}
                    style={{ width: 44, height: 44, borderRadius: '5px', border: `2px solid ${!memorialConfig.hero_photo ? accent : theme.border}`, background: theme.inputBg, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.textSub, fontSize: '0.6rem' }}>None</button>
                  {mediaFiles.map(f => (
                    <button key={f.filename} onClick={() => setMemorialConfig(c => ({ ...c, hero_photo: f.filename }))}
                      style={{ width: 44, height: 44, borderRadius: '5px', border: `2px solid ${memorialConfig.hero_photo === f.filename ? accent : theme.border}`, padding: 0, overflow: 'hidden', cursor: 'pointer' }}>
                      <img src={`${BACKEND_URL}/memorial/media/${encodeURIComponent(root)}/file/${encodeURIComponent(f.filename)}`} alt={f.filename} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    </button>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '0.6rem', color: theme.textSub }}>Upload photos in the Media Library tab first.</div>
              )}
            </div>

            {/* Sections */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '5px', textTransform: 'uppercase' }}>Sections</div>
              {memorialConfig.sections.map((sec, idx) => {
                const dataCount = memorialData ? ({
                  hero: 1,
                  biography: (memorialData.subject?.notes ? 1 : 0),
                  family: memorialData.family?.length || 0,
                  timeline: memorialData.timeline?.length || 0,
                  places: memorialData.places?.length || 0,
                  quotes: memorialData.quotes?.length || 0,
                }[sec.id] || 0) : '?';
                return (
                  <div key={sec.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 0', borderBottom: idx < memorialConfig.sections.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
                    <button onClick={() => setMemorialConfig(c => ({ ...c, sections: c.sections.map(s => s.id === sec.id ? { ...s, enabled: !s.enabled } : s) }))}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: sec.enabled ? accent : theme.textSub, flexShrink: 0 }}>
                      <Icon icon={sec.enabled ? ToggleRight : ToggleLeft} size={18} />
                    </button>
                    <span style={{ flex: 1, fontSize: theme.fsBadge, color: sec.enabled ? theme.text : theme.textSub, fontWeight: 600, opacity: sec.enabled ? 1 : 0.5 }}>{sec.label}</span>
                    <span style={{ fontSize: '0.6rem', color: theme.textSub, background: theme.inputBg, borderRadius: '8px', padding: '1px 6px', minWidth: '18px', textAlign: 'center' }}>{dataCount}</span>
                  </div>
                );
              })}
            </div>

            {/* Footer text */}
            <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '4px', textTransform: 'uppercase' }}>Footer Text</div>
              <Input theme={theme} value={memorialConfig.custom_footer} onChange={e => setMemorialConfig(c => ({ ...c, custom_footer: e.target.value }))} placeholder="Giving Words to Voices™ — Memorial Archive" />
            </div>

            {/* Data summary */}
            {memorialData && (
              <div style={{ background: theme.card, borderRadius: '7px', padding: '7px 8px' }}>
                <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '5px', textTransform: 'uppercase' }}>Data Available</div>
                <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                  {[
                    ['Subject', memorialData.subject?.display_name ? 1 : 0],
                    ['Family', memorialData.family?.length || 0],
                    ['Timeline', memorialData.timeline?.length || 0],
                    ['Places', memorialData.places?.length || 0],
                    ['Quotes', memorialData.quotes?.length || 0],
                  ].map(([label, count]) => (
                    <span key={label} style={{ fontSize: '0.6rem', padding: '2px 6px', borderRadius: '8px', background: count > 0 ? `${accent}20` : theme.inputBg, color: count > 0 ? accent : theme.textSub, fontWeight: 600 }}>
                      {label}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{ width: '1px', background: theme.border, margin: '20px 0', flexShrink: 0, alignSelf: 'flex-start', height: 'calc(100% - 140px)' }} />

          {/* RIGHT — Preview + actions */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '14px', gap: '10px', overflow: 'hidden' }}>

            {/* Preview */}
            <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: theme.card, borderRadius: '8px', padding: '10px', overflow: 'hidden' }}>
              <div style={{ fontSize: theme.fsBadge, color: theme.textSub, fontWeight: 700, marginBottom: '8px', textTransform: 'uppercase', flexShrink: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icon icon={Eye} size={12} /> Page Preview
              </div>
              <div style={{ flex: 1, borderRadius: '6px', border: `1px solid ${theme.border}`, overflow: 'hidden', background: '#fff' }}>
                {memorialData ? (
                  <iframe
                    ref={memorialIframeRef}
                    title="Memorial Preview"
                    srcDoc={buildMemorialPreviewHTML(memorialData, memorialConfig, BACKEND_URL, root)}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    sandbox="allow-same-origin"
                  />
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: theme.textSub, fontSize: theme.fsBadge }}>
                    {memorialLoaded ? 'No data available for this subject.' : <><Icon icon={Loader} size={14} /> Loading data...</>}
                  </div>
                )}
              </div>
            </div>

            {/* Public URL */}
            {memorialToken && (() => {
              const token = encodeURIComponent(memorialToken);
              const publicUrl = /^https?:/.test(BACKEND_URL)
                ? `${BACKEND_URL}/memorial/view/${token}`
                : window.location.origin + BACKEND_URL + '/memorial/view/' + token;
              return (
              <div style={{ flexShrink: 0, background: theme.card, borderRadius: '7px', padding: '7px 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Icon icon={Globe} size={12} color={accent} />
                <span style={{ fontSize: '0.6rem', color: theme.textSub, fontWeight: 600 }}>Public URL:</span>
                <a href={publicUrl} target="_blank" rel="noopener noreferrer"
                  style={{ flex: 1, fontSize: '0.6rem', color: accent, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', textDecoration: 'underline', cursor: 'pointer' }}>
                  {publicUrl}
                </a>
                <button onClick={() => { navigator.clipboard.writeText(publicUrl); }}
                  title="Copy URL" style={{ background: theme.inputBg, border: `1px solid ${theme.border}`, borderRadius: '4px', cursor: 'pointer', color: theme.textSub, padding: '2px 6px', flexShrink: 0, fontSize: '0.55rem', fontWeight: 600 }}>
                  Copy
                </button>
              </div>
              );
            })()}
            {memorialToken && (
              <div style={{ flexShrink: 0, background: theme.card, borderRadius: '7px', padding: '7px 10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ fontSize: '0.6rem', color: theme.textSub, fontWeight: 600 }}>Quotes:</span>
                <a href={`${BACKEND_URL}/memorial/quotes/${encodeURIComponent(memorialToken)}`} target="_blank" rel="noopener noreferrer"
                  style={{ flex: 1, fontSize: '0.6rem', color: '#f472b6', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', textDecoration: 'underline', cursor: 'pointer' }}>
                  /api/memorial/quotes/{memorialToken}
                </a>
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
              <button onClick={saveMemorialPage} disabled={memorialSaving} style={{ flex: 1, padding: '11px', background: memorialSaved ? theme.success : accent, color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, cursor: memorialSaving ? 'wait' : 'pointer', fontSize: theme.fsButton, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px' }}>
                {memorialSaved ? <><Icon icon={Check} size={14} /> SAVED</> : memorialSaving ? <><Icon icon={Loader} size={14} /> SAVING...</> : <><Icon icon={Save} size={14} /> SAVE & GENERATE</>}
              </button>
              <Button theme={theme} variant="secondary" size="md" icon={Download} onClick={exportMemorialHTML} disabled={!memorialToken} title="Download standalone HTML">HTML</Button>
            </div>
            {errorBanner}
            <div style={{ fontSize: '0.6rem', color: theme.textSub, textAlign: 'center', lineHeight: 1.4, flexShrink: 0 }}>Save generates the memorial page and makes it available at the public URL. Export downloads as a standalone HTML file.</div>
          </div>
        </div>
      )}

      {/* ══ TAB: Media Library ════════════════════════════════════ */}
      {activeTab === 'media' && (
        <div style={{ flex: 1, overflow: 'auto', padding: '14px' }}>
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>

            {/* Upload area */}
            <label
              onDrop={handleMediaDrop}
              onDragOver={e => { e.preventDefault(); setIsDraggingMedia(true); }}
              onDragLeave={() => setIsDraggingMedia(false)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px',
                padding: '24px', marginBottom: '14px',
                border: `2px dashed ${isDraggingMedia ? accent : theme.border}`,
                borderRadius: '10px', cursor: 'pointer',
                background: isDraggingMedia ? `${accent}08` : theme.card,
                transition: '0.15s',
              }}
            >
              {mediaUploading ? (
                <><Icon icon={Loader} size={24} color={accent} /><span style={{ fontSize: theme.fsBadge, color: accent }}>Uploading...</span></>
              ) : (
                <>
                  <Icon icon={Upload} size={24} color={isDraggingMedia ? accent : theme.textSub} />
                  <span style={{ fontSize: theme.fsBadge, color: isDraggingMedia ? accent : theme.textSub, fontWeight: 600 }}>
                    {isDraggingMedia ? 'Drop to upload' : 'Drag & drop or click to upload photos'}
                  </span>
                  <span style={{ fontSize: '0.6rem', color: theme.textSub }}>PNG, JPG, SVG, WebP, GIF — max 10MB</span>
                </>
              )}
              <input type="file" disabled={mediaUploading} accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif" onChange={e => { const f = e.target.files?.[0]; if (f) uploadMedia(f); e.target.value = ''; }} style={{ display: 'none' }} />
            </label>

            {actionError && <div style={{ marginBottom: '14px' }}>{errorBanner}</div>}

            {/* File grid */}
            {mediaLoading ? (
              <div style={{ textAlign: 'center', padding: '30px', color: theme.textSub }}><Icon icon={Loader} size={20} /> Loading...</div>
            ) : mediaFiles.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '30px', color: theme.textSub, fontSize: theme.fsBadge }}>No media files yet. Upload photos for use in memorial pages and markers.</div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '10px' }}>
                {mediaFiles.map(f => (
                  <div key={f.filename} style={{ background: theme.card, borderRadius: '8px', border: `1px solid ${theme.border}`, overflow: 'hidden' }}>
                    <div style={{ width: '100%', height: '120px', background: '#f0f0f0', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <img src={`${BACKEND_URL}/memorial/media/${encodeURIComponent(root)}/file/${encodeURIComponent(f.filename)}`} alt={f.filename} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                    </div>
                    <div style={{ padding: '6px 8px' }}>
                      <div style={{ fontSize: '0.6rem', fontWeight: 600, color: theme.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                        <span style={{ fontSize: '0.55rem', color: theme.textSub }}>{(f.size / 1024).toFixed(0)} KB</span>
                        <button onClick={() => deleteMedia(f.filename)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }} title="Delete"><Icon icon={Trash2} size={12} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      </div>{/* end center column */}

      {/* Right spacer */}
      <div style={{ flex: 1 }} />

    </div>
  );
}
